<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">Logs Management</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active"><i class="fas fa-file-alt"></i> Logs</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <b-row class="col-lg-12">
          <div class="col-lg-4 col-md-4 col-sm-12 mb-4">
            <b-form-input id="uid-filter-input" v-model="filters.uid" autocomplete="off" placeholder="Enter UID"
              :required="!filters.userKey" :state="validateField('uid')" @blur="handleFieldTouch('uid')" />
            <b-form-invalid-feedback :state="validateField('uid')" v-if="touchedFields.uid && !filters.userKey">
              This field is required
            </b-form-invalid-feedback>
          </div>
          <div class="col-lg-4 col-md-4 col-sm-12 mb-4">
            <b-form-input id="userKey-filter-input" v-model="filters.userKey" autocomplete="off" placeholder="Enter User Key"
              :required="!filters.uid" :state="validateField('userKey')" @blur="handleFieldTouch('userKey')" />
            <b-form-invalid-feedback :state="validateField('userKey')" v-if="touchedFields.userKey && !filters.uid">
              This field is required
            </b-form-invalid-feedback>
          </div>
          <div class="col-lg-4 col-md-4 col-sm-12">
            <single-field-operator v-model="filters.operator" id="operator-filter-input"
              :state="validateField('operator')" :touched="touchedFields.operator"
              @blur="handleFieldTouch('operator')" />
          </div>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-2 col-md-2 col-sm-12">
            <b-form-input id="roundId-filter-input" v-model="filters.roundId" autocomplete="off"
              placeholder="Enter Round ID" />
          </div>
          <div class="col-lg-2 col-md-2 col-sm-12">
            <b-form-input id="transactionId-filter-input" v-model="filters.transactionId" autocomplete="off"
              placeholder="Enter Transaction ID" />
          </div>
          <div class="col-lg-8 col-md-8 col-sm-12">
            <div class="d-flex">
              <b-form-datepicker v-model="filters.date" :max="maxDate" class="mr-2"
                :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                locale="en-GB"></b-form-datepicker>
              <div class="time-group">
                <b-form-input id="start-time-filter-input" v-model="filters.startTime" type="time" step="1"
                  class="time-filter-input mb-2 mr-2" :disabled="!filters.date" @blur="onTimeBlur('startTime')" />
                <b-form-input id="end-time-filter-input" v-model="filters.endTime" type="time" step="1"
                  class="time-filter-input mb-2" :disabled="!filters.date" @blur="onTimeBlur('endTime')" />
                <span class="utc-label">UTC</span>
              </div>
            </div>
            <b-form-invalid-feedback :state="validateTimeRange" v-if="filters.date">
              Time range cannot exceed 1 hour
            </b-form-invalid-feedback>
          </div>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <b-button variant="primary" @click="loadLogs" :disabled="!isFormValid">
              Search Logs
            </b-button>
            <b-button variant="secondary" @click="clearFilters">Clear</b-button>
          </div>
        </b-row>

        <div class="row col-12 mt-4" id="loading-container">
          <div class="col-12 mb-10">
            <div class="table-wrapper mb-10">
              <div v-if="isLoading" class="loading-overlay">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
              </div>
              <b-table :striped="true" :small="true" :hover="true" :items="logs" :fields="fields" :sort-by="sortBy"
                :sort-desc="sortDesc" :no-local-sorting="true" :sort-icon-left="true" @sort-changed="sortingChanged"
                :busy="isLoading">
                <template #cell(roundId)="row">
                  {{ formatRoundId(row.value) }}
                </template>
                <template #cell(actions)="row">
                  <b-button size="sm" variant="info" @click="showLogDetails(row.item)">Show</b-button>
                </template>
              </b-table>
              <div v-if="!isLoading && (!logs || logs.length === 0)" class="text-center my-4">
                <h4 class="text-muted">No logs found</h4>
              </div>
            </div>
          </div>
        </div>

        <b-modal v-model="showModal" size="lg" title="Log Details" scrollable>
          <div v-if="selectedLog">
            <div>
              <p><b>POST:</b> {{ postUrl }}</p>
              <p><b>URL:</b> {{ inputUrl }}</p>
              <p>
                <b>Body:</b>
              <pre>{{ requestBodyFormatted }}</pre>
              </p>
              <p>
                <b>Response:</b>
              <pre>{{ responseBodyFormatted }}</pre>
              </p>
            </div>
          </div>
          <template #modal-footer>
            <div>
              <b-button variant="secondary" class="mr-2" @click="hideModal">Close</b-button>
              <b-button variant="primary" @click="downloadLogDetails">Download</b-button>
            </div>
          </template>
        </b-modal>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import SingleFieldOperator from '../../components/SingleFieldOperator.vue';

export default {
  components: {
    SingleFieldOperator
  },
  data() {
    return {
      filters: {
        uid: '',
        userKey: '',
        roundId: '',
        operator: '',
        transactionId: '',
        date: '',
        startTime: '12:00:00',
        endTime: '13:00:00'
      },
      touchedFields: {
        uid: false,
        userKey: false,
        roundId: false,
        operator: false,
        timeRange: false
      },
      logs: [],
      isLoading: false,
      fields: [
        { key: 'timestamp', label: 'Timestamp' },
        { key: 'uid', label: 'UID' },
        { key: 'roundId', label: 'Round ID' },
        { key: 'message', label: 'Message' },
        { key: 'actions', label: 'Actions' }
      ],
      sortBy: 'timestamp',
      sortDesc: true,
      showModal: false,
      selectedLog: null,
    };
  },
  computed: {
    TIME_LIMIT_MS() {
      return 60 * 60 * 1000;
    },
    isFormValid() {
      const requiredFieldsValid = (this.filters.uid.trim() !== '' || this.filters.userKey.trim() !== '') &&
        this.filters.operator.trim() !== '';

      const optionalFieldsValid = this.filters.roundId.trim() !== '' ||
        this.filters.transactionId.trim() !== '' ||
        this.validateTimeRange;

      return requiredFieldsValid && optionalFieldsValid;
    },
    maxDate() {
      return new Date().toISOString().split('T')[0];
    },
    validateTimeRange() {
      if (!this.filters.date) {
        return null;
      }
      if (!this.filters.startTime || !this.filters.endTime) {
        return false;
      }

      const startDateTime = this.parseTimeAsUtcDate(this.filters.date, this.filters.startTime);
      const endDateTime = this.parseTimeAsUtcDate(this.filters.date, this.filters.endTime);

      if (!startDateTime || !endDateTime) {
        return false;
      }

      const diffInMs = endDateTime - startDateTime;

      return diffInMs > 0 && diffInMs <= this.TIME_LIMIT_MS;
    },
    inputUrl() {
      if (!this.selectedLog) {
        return '';
      }
      const { details } = this.selectedLog;
      return details.inTransaction?.original_input?.url
        || details.input?.url
        || '';
    },
    postUrl() {
      if (!this.selectedLog) {
        return '';
      }
      return this.selectedLog.details.inTransaction?.original_input?.headers?.url || '';
    },
    requestBodyFormatted() {
      if (!this.selectedLog) return '';
      return this.formatJson(this.getRequestBody(this.selectedLog));
    },
    responseBodyFormatted() {
      if (!this.selectedLog) return '';
      return this.formatJson(this.getResponseBody(this.selectedLog));
    },
  },
  methods: {
    validateField(value) {
      if (value === 'uid' && this.filters.userKey.trim() !== '') {
        return true; // uid не обязателен, если заполнен userKey
      }
      if (value === 'userKey' && this.filters.uid.trim() !== '') {
        return true; // userKey не обязателен, если заполнен uid
      }
      return this.touchedFields[value] ? (this.filters[value].trim() !== '') : null;
    },
    handleFieldTouch(field) {
      this.touchedFields[field] = true;
      if (field === 'startTime' || field === 'endTime') {
        this.touchedFields.timeRange = true;
      }
    },
    normalizeTimeValue(value) {
      if (!value || typeof value !== 'string') {
        return '';
      }

      const trimmed = value.trim();
      if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) {
        return trimmed;
      }
      if (/^\d{2}:\d{2}$/.test(trimmed)) {
        return `${trimmed}:00`;
      }

      return trimmed;
    },
    onTimeBlur(field) {
      this.filters[field] = this.normalizeTimeValue(this.filters[field]);
      this.handleFieldTouch(field);
    },
    parseTimeAsUtcDate(dateStr, timeStr) {
      const normalized = this.normalizeTimeValue(timeStr);
      if (!dateStr || !/^\d{2}:\d{2}:\d{2}$/.test(normalized)) {
        return null;
      }

      const date = new Date(`${dateStr}T${normalized}Z`);

      return Number.isNaN(date.getTime()) ? null : date;
    },
    loadLogs() {
      if (!this.isFormValid) {
        return;
      }
      this.isLoading = true;
      this.clearResults();

      let startDateTime = null;
      let endDateTime = null;

      if (this.filters.date && this.filters.startTime) {
        startDateTime = this.parseTimeAsUtcDate(this.filters.date, this.filters.startTime);
      }

      if (this.filters.date && this.filters.endTime) {
        endDateTime = this.parseTimeAsUtcDate(this.filters.date, this.filters.endTime);
      }

      axios.get('/api/v1/logger', {
        params: {
          uid: this.filters.uid || null,
          userKey: this.filters.userKey || null,
          roundId: this.filters.roundId || null,
          operator: this.filters.operator,
          transactionId: this.filters.transactionId || null,
          startTime: startDateTime ? startDateTime.toISOString() : null,
          endTime: endDateTime ? endDateTime.toISOString() : null
        }
      })
        .then(response => {
          this.logs = response.data;
          this.$toastr.i("Logs loaded successfully");
        })
        .catch((err) => {
          console.error(err);
          const errorMessage = err.response?.data?.error || "Failed to load logs";
          this.$toastr.e(errorMessage);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    clearFilters() {
      this.filters = {
        uid: '',
        userKey: '',
        roundId: '',
        operator: '',
        transactionId: '',
        date: '',
        startTime: '12:00:00',
        endTime: '13:00:00'
      };
      this.touchedFields = {
        uid: false,
        userKey: false,
        roundId: false,
        operator: false,
        timeRange: false
      };
      this.logs = [];
      this.selectedLog = null;
      this.showModal = false;
    },
    clearResults() {
      this.logs = [];
    },
    prepareBody(body) {
      if (body) {
        const keys = Object.keys(body);

        for (const key of keys) {
          const val = body[key];

          if (typeof val === 'object') {
            const objKeys = Object.keys(val);

            if (objKeys.length === 1 && objKeys[0][0] === '$') {
              body[key] = body[key][objKeys[0]];
            }
          }
        }
      }

      return body;
    },
    formatJson(data) {
      if (data === undefined || data === null) {
        return '';
      }
      return JSON.stringify(data, null, 2);
    },
    formatRoundId(value) {
      if (value === undefined || value === null || value === '') {
        return '';
      }
      if (typeof value === 'number') {
        return Number.isSafeInteger(value) ? String(value) : value.toLocaleString('fullwide', { useGrouping: false });
      }
      return String(value);
    },
    showLogDetails(log) {
      this.selectedLog = log;
      this.showModal = true;
    },
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
    },

    getRequestBody(log) {
      const { details } = log;
      const inTx = details.inTransaction || {};

      return inTx.original_input?.original_request?.data?.softswiss
        || inTx.original_input?.body
        || inTx.input
        || details.input?.payload
        || details.input
        || null;
    },
    getResponseBody(log) {
      const { details } = log;
      const inTx = details.inTransaction || {};

      return this.prepareBody(
        inTx.original_output?.original_response
        || inTx.original_output?.body
        || inTx.output
        || details.output
        || null
      );
    },
    hideModal() {
      this.showModal = false;
    },
    downloadLogDetails() {
      if (!this.selectedLog) return;

      // Получаем имя оператора из store
      const operatorInfo = this.$store.getters['limits/operatorsForSelect']?.find(
        op => op.value === this.filters.operator
      );
      const operatorName = operatorInfo?.label || 'unknown';

      // Формируем содержимое файла
      const content = [
        'Search Parameters:',
        `UID: ${this.filters.uid}`,
        `Operator: ${operatorName} (${this.filters.operator})`,
        `Round ID: ${this.filters.roundId || 'Not specified'}`,
        `Transaction ID: ${this.filters.transactionId || 'Not specified'}`,
        `Date: ${this.filters.date || 'Not specified'}`,
        `Time Range: ${this.filters.startTime} - ${this.filters.endTime}`,
        '\nLog Details:',
        `POST: ${this.postUrl}`,
        `URL: ${this.inputUrl}`,
        '\nBody:',
        this.requestBodyFormatted,
        '\nResponse:',
        this.responseBodyFormatted
      ].join('\n');

      // Создаем имя файла
      let additionalParams = [];
      if (this.filters.roundId) {
        additionalParams.push(`round_${this.filters.roundId}`);
      }
      if (this.filters.transactionId) {
        additionalParams.push(`trans_${this.filters.transactionId}`);
      }
      if (this.filters.date) {
        additionalParams.push(
          `date_${this.filters.date}_${this.filters.startTime.replace(':', '')}_${this.filters.endTime.replace(':', '')}`
        );
      }

      const additionalInfo = additionalParams.length > 0
        ? '_' + additionalParams.join('_')
        : '';

      const fileName = `${this.filters.uid}_${operatorName}${additionalInfo}.txt`;

      // Создаем blob и скачиваем файл
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },

};
</script>

<style scoped>
.d-flex {
  align-items: flex-start;
}

.form-group.mb-0 {
  margin-bottom: 0;
}

/* Подстраиваем ширину полей выбора даты и времени */
.b-form-datepicker {
  width: 200px;
  min-width: 200px;
  font-size: 0.875rem;
}

.time-filter-input {
  width: 132px;
  min-width: 132px;
  font-size: 0.875rem;
}

/* Уменьшаем размер шрифта в выпадающих списках */
::v-deep .b-form-datepicker .dropdown-menu {
  font-size: 0.875rem;
}

/* Уменьшаем размер кнопок в пикере даты */
::v-deep .b-form-datepicker button {
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
}

.invalid-feedback {
  position: absolute;
  margin-top: 2px;
}

.form-group {
  margin-bottom: 25px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.table-wrapper {
  position: relative;
  margin-bottom: 10px;
}

.apply-clear {
  margin-top: 1rem;
}

.apply-clear button {
  margin-right: 0.5rem;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  max-height: 300px;
  overflow-y: auto;
}

.time-group {
  position: relative;
  display: flex;
  align-items: flex-start;
}

.utc-label {
  position: absolute;
  right: -30px;
  top: 8px;
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: bold;
}
</style>
